// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import { ApplicationInsights, IExceptionTelemetry, ITelemetryItem } from '@microsoft/applicationinsights-web';
import { cloneDeep } from 'lodash';

import appInsightsConfig from './config';
import { REQUEST_RESPONSE_EVENT_NAME } from './constants';
import { createException, createPageAction, createRequestResponse, createTraceEvent } from './customTelemetryCreators';
import { telemetryInitializer } from './telemetryInitializer';
import Environment from './../../environments';
import CorrelationVector from './../../lib/CorrelationVector';
import { msalInstance } from '../../../src/index';

const appInsights = new ApplicationInsights({config: appInsightsConfig});
appInsights.loadAppInsights();

export const configureAppInsightsWithCV = (cv: string) => {
    if (appInsights.context) {
        appInsights.context.telemetryTrace.parentID = cv.substring(0, cv.indexOf('.'));
    }
};
configureAppInsightsWithCV(CorrelationVector.getCurrentValue());

function changeTelemetryItemDetailsToDevKey(telemetryItem: ITelemetryItem): void {
    telemetryItem.iKey = Environment.developerTelemetryKey;
    telemetryItem.name = telemetryItem.name.replace(
        Environment.telemetryKey.replace(/-/g, ''),
        Environment.developerTelemetryKey.replace(/-/g, '')
    );
    const accounts = msalInstance.getAllAccounts();
    if (telemetryItem.tags && accounts.length > 0) {
        telemetryItem.tags['ai.user.authUserId'] = accounts && accounts[0] && accounts[0].username;
    }
}

// https://github.com/microsoft/ApplicationInsights-JS/issues/1078
// Having 2 App Insights instances doesn't seem to work (auto collected telemetry seems to only go through the last instantiated instance)
// so just cloning the telemetry
// @ts-ignore
XMLHttpRequest.prototype.realSend = XMLHttpRequest.prototype.send;
XMLHttpRequest.prototype.send = function(value: any) {
    let parsedValue = value;
    let valueToSend = value;
    try {
        parsedValue = JSON.parse(value);
    } catch {
        // @ts-ignore
        this.realSend(value);
        return;
    }
    
    // If parsed value is an array
    if (Array.isArray(parsedValue)) {
        const telemetryToSend: any[] = [];
        parsedValue.forEach((telemetryItem: ITelemetryItem) => {
            // It's a telemetry item
            if (telemetryItem && telemetryItem.iKey) {
                // If this is a request/response telemetry item, then just update the key to the 2nd app insights
                if (telemetryItem.data && (telemetryItem.data as any).baseData
                    && (telemetryItem.data as any).baseData.name === REQUEST_RESPONSE_EVENT_NAME) {
                    changeTelemetryItemDetailsToDevKey(telemetryItem);
                } else {
                    const clonedItem = cloneDeep(telemetryItem);
                    changeTelemetryItemDetailsToDevKey(clonedItem);
                    telemetryToSend.push(clonedItem);
                }

                telemetryToSend.push(telemetryItem);
            }
        });

        if (telemetryToSend.length) {
            valueToSend = JSON.stringify(telemetryToSend);
        }
    } else if (parsedValue && parsedValue.iKey) {
        // Else if parsed value is single telemetry item
        const telemetryItem = parsedValue;
        if (telemetryItem.data && (telemetryItem.data as any).baseData
            && (telemetryItem.data as any).baseData.name === REQUEST_RESPONSE_EVENT_NAME) {
            // If this is a request/response telemetry item, then just update the key to the 2nd app insights
            changeTelemetryItemDetailsToDevKey(telemetryItem);
            
            // TODO: #23719972 Probably don't need to have if-else here, the original value is most likely always a string, but requires more investigation
            // Re-stringify the value if original value was string
            if (typeof valueToSend === 'string') {
                valueToSend = JSON.stringify(telemetryItem);
            } else {
                valueToSend = telemetryItem;
            }
        } else {
            // Else clone the item, update the key to dev key, and send it out
            const clonedItem = cloneDeep(telemetryItem);
            changeTelemetryItemDetailsToDevKey(clonedItem);
            
            // TODO: #23719972 Probably don't need to have if-else here, the original value is most likely always a string, but requires more investigation
            // Re-stringify the value if original value was string
            if (typeof valueToSend === 'string') {
                // @ts-ignore
                this.realSend(JSON.stringify(clonedItem));
            } else {
                // @ts-ignore
                this.realSend(clonedItem);
            }
        }
    }

    // @ts-ignore
    this.realSend(valueToSend);
};

// @ts-ignore
// export const logException: typeof createException = (...args: any[]) => {
//     // @ts-ignore
//     const telemetryItem = createException(...args);
//     telemetryItem && appInsights.trackException(telemetryItem);
//     return telemetryItem;
// };

export const logException: typeof createException = (correlationVector: string, exception?: any, errorInfo?: any, handled?: boolean = true) => {
    try{
        const telemetryItem: IExceptionTelemetry = createException(correlationVector, exception, errorInfo, handled);
        telemetryItem && appInsights.trackException(telemetryItem);
        return telemetryItem;
    }
    catch(e)
    {
        console.log(e);
    }
};

// @ts-ignore
export const logPageAction: typeof createPageAction = (...args: any[]) => {
    // @ts-ignore
    const telemetryItem = createPageAction(...args);
    telemetryItem && appInsights.trackEvent(telemetryItem);
    return telemetryItem;
};

// @ts-ignore
export const logRequestResponse: typeof createRequestResponse = (...args: any[]) => {
    // @ts-ignore
    const telemetryItem = createRequestResponse(...args);
    if (telemetryItem) {
        appInsights.trackEvent(telemetryItem);
        // Force flush just in case
        appInsights.flush();
    }
    return telemetryItem;
};

// @ts-ignore
export const logTraceEvent: typeof createTraceEvent = (...args: any[]) => {
    // @ts-ignore
    const telemetryItem = createTraceEvent(...args);
    if (telemetryItem) {
        appInsights.trackTrace(telemetryItem);
        // Force flush just in case
        appInsights.flush();
    }
    return telemetryItem;
};

appInsights.addTelemetryInitializer(telemetryInitializer);
appInsights.onunloadFlush();
export default appInsights;
